import React from "react";
import ActiveCouponsIcon from "../../../assets/icons/active-coupons-icon.svg";
import EyeIcon from "../../../assets/icons/total-viewed-coupons-icon.svg";
import type { MerchantStatisticsResponse } from "../types/merchant-home-data.types";
import { resolveExpiringSoonCount } from "../utils/merchantHomeViewModels";
import { MerchantHomeErrorState } from "./MerchantHomeErrorState";
import { MetricCard } from "./MetricCard";

interface MerchantHomeStatsSectionProps {
  userName: string;
  currentDate: string;
  statistics: MerchantStatisticsResponse | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  onRetry: () => void;
  onRefreshAll: () => void;
}

const MetricCardSkeleton: React.FC = () => (
  <div className="h-full min-h-[112px] animate-pulse rounded-lg bg-white outline outline-1 outline-offset-[-1px] outline-neutral-200 dark:bg-gray-800 dark:outline-gray-700" />
);

export const MerchantHomeStatsSection: React.FC<MerchantHomeStatsSectionProps> = ({
  userName,
  currentDate,
  statistics,
  loading,
  refreshing,
  error,
  onRetry,
  onRefreshAll,
}) => {
  const expiringSoonCount = resolveExpiringSoonCount(statistics?.expiring_soon);
  const conversionRate = statistics?.conversion_rate ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2.5">
          <div
            className="text-neutral-500 text-xl font-normal leading-6"
            style={{ fontFamily: "Cal Sans" }}
          >
            {currentDate}
          </div>
          <div
            className="text-[#004232] dark:text-emerald-400 text-3xl font-normal leading-8"
            style={{ fontFamily: "Cal Sans" }}
          >
            Welcome, {userName}.
          </div>
        </div>
        <button
          type="button"
          onClick={onRefreshAll}
          disabled={refreshing || loading}
          className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-[#004232] transition-opacity hover:opacity-80 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        >
          Refresh
        </button>
      </div>

      {error && statistics ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          <span>{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="font-semibold hover:opacity-80"
          >
            Retry
          </button>
        </div>
      ) : null}

      {loading && !statistics ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
        </div>
      ) : error && !statistics ? (
        <MerchantHomeErrorState
          message={error}
          onRetry={onRetry}
        />
      ) : statistics ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          <MetricCard
            label="Active Coupons"
            value={statistics.active_coupons}
            icon={
              <img
                src={ActiveCouponsIcon}
                alt="Active Coupons"
                className="w-5 h-5 dark:opacity-80"
              />
            }
            valueColor={expiringSoonCount > 0 ? "orange" : "default"}
            badge={{
              text: `${expiringSoonCount} expiring soon`,
              variant: "error",
            }}
            footer={{
              text: "next 7 days",
            }}
            customHeight="h-28"
          />
          <MetricCard
            label="Total Coupon Views"
            value={statistics.total_coupon_views}
            icon={
              <img
                src={EyeIcon}
                alt="Views"
                className="w-5 h-5 dark:opacity-80"
              />
            }
            footer={{
              text: "This week",
            }}
          />
          <MetricCard
            label="Total Redemptions"
            value={statistics.total_redemptions}
            icon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  d="M9.63421 2.72156C9.67018 2.65623 9.72303 2.60176 9.78723 2.56382C9.85143 2.52587 9.92464 2.50586 9.99921 2.50586C10.0738 2.50586 10.147 2.52587 10.2112 2.56382C10.2754 2.60176 10.3282 2.65623 10.3642 2.72156L12.8242 7.39156C12.8829 7.4997 12.9648 7.5935 13.064 7.66625C13.1632 7.73899 13.2773 7.78886 13.398 7.81228C13.5188 7.8357 13.6433 7.83209 13.7625 7.80171C13.8817 7.77133 13.9927 7.71493 14.0875 7.63656L17.6517 4.58323C17.7201 4.52758 17.8044 4.49508 17.8925 4.4904C17.9806 4.48572 18.0679 4.5091 18.1418 4.55718C18.2157 4.60527 18.2725 4.67557 18.3039 4.75797C18.3354 4.84037 18.3398 4.93062 18.3167 5.01573L15.955 13.5541C15.9068 13.7288 15.803 13.883 15.6592 13.9934C15.5155 14.1038 15.3396 14.1643 15.1584 14.1657H4.84088C4.65949 14.1645 4.48347 14.104 4.33954 13.9937C4.19561 13.8833 4.09163 13.7289 4.04338 13.5541L1.68255 5.01656C1.65942 4.93146 1.6639 4.84121 1.69533 4.75881C1.72676 4.6764 1.78353 4.6061 1.85746 4.55802C1.9314 4.50993 2.01868 4.48655 2.10675 4.49123C2.19482 4.49591 2.27913 4.52841 2.34755 4.58406L5.91088 7.6374C6.00571 7.71576 6.11672 7.77216 6.23593 7.80254C6.35515 7.83293 6.47961 7.83653 6.60038 7.81311C6.72115 7.78969 6.83524 7.73982 6.93446 7.66708C7.03367 7.59434 7.11555 7.50053 7.17421 7.3924L9.63421 2.72156Z"
                  stroke="#00C49A"
                  strokeWidth="1.39167"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="dark:stroke-emerald-400"
                />
                <path
                  d="M4.16602 17.5H15.8327"
                  stroke="#00C49A"
                  strokeWidth="1.39167"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="dark:stroke-emerald-400"
                />
              </svg>
            }
            footer={{
              text: "This week",
            }}
          />
          <MetricCard
            label="Conversion Rate"
            value={`${conversionRate.toFixed(1)}%`}
            icon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  d="M1.66602 7.49984C2.32906 7.49984 2.96494 7.76323 3.43378 8.23207C3.90262 8.70091 4.16602 9.3368 4.16602 9.99984C4.16602 10.6629 3.90262 11.2988 3.43378 11.7676C2.96494 12.2364 2.32906 12.4998 1.66602 12.4998V14.1665C1.66602 14.6085 1.84161 15.0325 2.15417 15.345C2.46673 15.6576 2.89065 15.8332 3.33268 15.8332H16.666C17.108 15.8332 17.532 15.6576 17.8445 15.345C18.1571 15.0325 18.3327 14.6085 18.3327 14.1665V12.4998C17.6696 12.4998 17.0338 12.2364 16.5649 11.7676C16.0961 11.2988 15.8327 10.6629 15.8327 9.99984C15.8327 9.3368 16.0961 8.70091 16.5649 8.23207C17.0338 7.76323 17.6696 7.49984 18.3327 7.49984V5.83317C18.3327 5.39114 18.1571 4.96722 17.8445 4.65466C17.532 4.3421 17.108 4.1665 16.666 4.1665H3.33268C2.89065 4.1665 2.46673 4.3421 2.15417 4.65466C1.84161 4.96722 1.66602 5.39114 1.66602 5.83317V7.49984Z"
                  stroke="#00C49A"
                  strokeWidth="1.35"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="dark:stroke-emerald-400"
                />
                <path
                  d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335"
                  stroke="#00C49A"
                  strokeWidth="1.35"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="dark:stroke-emerald-400"
                />
              </svg>
            }
            footer={{
              text: "vs last week",
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
